import { useEffect, useState, type ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { setupApi, simulationApi, type SetupState } from './api';
import { useWaitingPlaces } from '../places/useWaitingPlaces';
import { usePreferences } from '../shell/preferencesContext';
import { Count, cx, Sidebar, SidebarGroup, SidebarItem } from '../ui';
import { BrandingPage } from './branding/BrandingPage';
import { CancellationReasonsPage } from './cancellations/CancellationReasonsPage';
import { DevicesPage } from './devices/DevicesPage';
import { DispatchPage } from './dispatch/DispatchPage';
import { DriverAppPage } from './driverApp/DriverAppPage';
import { DutyPage } from './duty/DutyPage';
import { FleetPage } from './fleet/FleetPage';
import { ImportPage } from './import/ImportPage';
import { LabelsPage } from './labels/LabelsPage';
import { MapPage } from './map/MapPage';
import { PlacesPage } from './places/PlacesPage';
import { PricesPage } from './prices/PricesPage';
import { ReplayPage } from './replay/ReplayPage';
import { AddressSearchPage } from './search/AddressSearchPage';
import { GROUPS, SECTIONS, type Section } from './sections';
import { onSetupChanged } from './setup/events';
import { SetupPage } from './setup/SetupPage';
import { SimulationPage } from './simulation/SimulationPage';
import { SurchargesPage } from './surcharges/SurchargesPage';
import { TelephonyPage } from './telephony/TelephonyPage';
import { ZonesPage } from './zones/ZonesPage';

/** The pages that put a map beside their settings, and so are not held to the column. */
const WIDE: ReadonlySet<Section> = new Set<Section>(['zones', 'search', 'places']);

const PAGES: Record<Section, ComponentType> = {
  setup: SetupPage,
  dispatch: DispatchPage,
  zones: ZonesPage,
  map: MapPage,
  search: AddressSearchPage,
  places: PlacesPage,
  cancellations: CancellationReasonsPage,
  prices: PricesPage,
  surcharges: SurchargesPage,
  duty: DutyPage,
  fleet: FleetPage,
  devices: DevicesPage,
  labels: LabelsPage,
  replay: ReplayPage,
  driverApp: DriverAppPage,
  branding: BrandingPage,
  telephony: TelephonyPage,
  import: ImportPage,
  simulation: SimulationPage,
};

/**
 * The settings area: a sidebar of grouped sections on the left, one page on the right.
 *
 * One page is mounted at a time rather than all of them hidden with CSS. Each loads its own
 * data, and ten screens fetching on open would be ten requests for the one the person
 * actually wanted.
 */
export function SettingsArea() {
  const { t } = useTranslation();
  const { section } = useParams<{ section: string }>();

  /**
   * Whether this deployment simulates firms.
   *
   * Asked once and hidden on any refusal — the console deliberately does not re-implement
   * authorisation by reading a role and guessing.
   */
  const [simulated, setSimulated] = useState(false);

  /**
   * How far this firm has got, for the entry at the head of the sidebar.
   *
   * Null for anyone who is not an administrator, because the endpoint refuses them and the
   * entry then does not exist — the same probe as the simulation section below. Asked again
   * whenever a screen says the setup changed, so marking an item on the Setup page moves the
   * number beside its own entry.
   */
  const [setup, setSetup] = useState<SetupState | null>(null);

  /** What waits for the administrator on the firm's addresses (#420), beside Адреси. */
  const waiting = useWaitingPlaces(usePreferences().access.settings);

  useEffect(() => {
    let cancelled = false;

    simulationApi
      .state()
      .then(() => {
        if (!cancelled) setSimulated(true);
      })
      .catch(() => undefined);

    const probe = () => {
      setupApi
        .state()
        .then((state) => {
          if (!cancelled) setSetup(state);
        })
        .catch(() => undefined);
    };

    probe();
    const stop = onSetupChanged(probe);

    return () => {
      cancelled = true;
      stop();
    };
  }, []);

  const current = (SECTIONS as readonly string[]).includes(section ?? '')
    ? (section as Section)
    : 'dispatch';

  const Page = PAGES[current];

  return (
    <div className="flex flex-1 min-h-0">
      <Sidebar>
        {setup !== null && (
          <SidebarGroup label={t('nav.settingsGroup.gettingStarted')}>
            <SidebarItem to="/settings/setup">
              {t('nav.settings.setup')}
              {setup.percent < 100 && (
                <span className="ml-auto text-secondary text-ink-3 tabular-nums">
                  {t('setup.percent', { percent: setup.percent })}
                </span>
              )}
            </SidebarItem>
          </SidebarGroup>
        )}

        {GROUPS.map((group) => (
          <SidebarGroup key={group.label} label={t(`nav.settingsGroup.${group.label}`)}>
            {group.sections.map((name) => (
              <SidebarItem key={name} to={`/settings/${name}`}>
                {t(`nav.settings.${name}`)}
                {name === 'places' && waiting !== null && waiting > 0 && (
                  <Count className="ml-auto">{waiting}</Count>
                )}
              </SidebarItem>
            ))}
          </SidebarGroup>
        ))}

        {simulated && (
          <SidebarGroup label={t('nav.settingsGroup.simulation')}>
            <SidebarItem to="/settings/simulation">{t('nav.settings.simulation')}</SidebarItem>
          </SidebarGroup>
        )}
      </Sidebar>

      <div className="flex-1 min-w-0 overflow-y-auto">
        {/* Settings read best in a column; a page that carries a map is let out of it and
            keeps its own controls to 900px. */}
        <div
          className={cx(
            WIDE.has(current) ? '' : 'max-w-[900px]',
            'flex flex-col gap-4 p-6',
          )}
        >
          <Page />
        </div>
      </div>
    </div>
  );
}
